"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Bot, AlertTriangle, Eye, Lightbulb, Shield, Zap, Users, FileText } from "lucide-react"
import { useEffect } from "react"

const sections = [
  {
    id: "ai-nature",
    title: "Nature of AI-Generated Content",
    icon: Bot,
    content: [
      {
        subtitle: "AI Technology Overview",
        text: "Roomvia uses artificial intelligence and machine learning technologies to generate interior design recommendations, visualizations, and suggestions. Our AI models are trained on large datasets of interior design images, style patterns, and design principles to provide personalized recommendations.",
      },
      {
        subtitle: "Algorithmic Generation",
        text: "AI-generated content is created through algorithmic processes and statistical patterns learned from training data. While our AI strives to produce high-quality, relevant, and aesthetically pleasing designs, the output is generated by computer algorithms, not human designers.",
      },
      {
        subtitle: "Design Suggestions",
        text: "The designs, color schemes, furniture arrangements, and other recommendations provided by our AI are suggestions based on pattern recognition and should be considered as inspiration and starting points for your design projects.",
      },
    ],
  },
  {
    id: "accuracy-limitations",
    title: "Accuracy and Limitations",
    icon: AlertTriangle,
    content: [
      {
        subtitle: "Accuracy",
        text: "AI recommendations may not always be accurate, appropriate, or suitable for your specific space, needs, or preferences. Room dimensions, lighting conditions, structural limitations, and other factors may not be fully captured or considered.",
      },
      {
        subtitle: "Feasibility",
        text: "Design suggestions may not always be practically feasible due to budget constraints, local building codes, structural limitations, or product availability in your area.",
      },
      {
        subtitle: "Style Interpretation",
        text: "AI interpretation of design styles may vary and may not perfectly match traditional or professional definitions of specific design aesthetics.",
      },
      {
        subtitle: "Spatial Understanding",
        text: "While our AI analyzes room photos, it may not fully understand complex spatial relationships, architectural features, or structural elements that could impact design implementation.",
      },
      {
        subtitle: "Personal Preferences",
        text: "AI cannot fully capture individual taste, lifestyle needs, or personal circumstances that may influence design decisions.",
      },
    ],
  },
  {
    id: "user-responsibility",
    title: "User Responsibility and Professional Consultation",
    icon: Shield,
    content: [
      {
        subtitle: "User Responsibility",
        text: "Users are solely responsible for evaluating the suitability, safety, and feasibility of any AI-generated designs or recommendations before implementation.",
      },
      {
        subtitle: "Professional Consultation",
        text: "We strongly recommend consulting with qualified professionals including licensed interior designers or architects for complex projects, contractors for structural modifications, electricians for lighting or electrical work, and local authorities for building permits and code compliance.",
      },
      {
        subtitle: "Safety Considerations",
        text: "Users must ensure that any implemented designs comply with local building codes, safety regulations, and accessibility requirements.",
      },
      {
        subtitle: "Budget and Planning",
        text: "AI cost estimates are approximations only. Actual costs may vary significantly based on location, quality of materials, labor costs, and other factors.",
      },
      {
        subtitle: "Implementation",
        text: "Roomvia is not responsible for the actual implementation of designs or any issues that may arise during renovation or decoration projects.",
      },
    ],
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property and AI Outputs",
    icon: FileText,
    content: [
      {
        subtitle: "Ownership of AI Outputs",
        text: "Design concepts and visualizations generated by our AI are provided under license for your personal or commercial use as specified in your subscription plan. However, similar designs may be generated for other users based on similar inputs.",
      },
      {
        subtitle: "Originality",
        text: "While our AI creates unique combinations and arrangements, individual elements, styles, or concepts may not be entirely original and could be similar to existing designs, trends, or patterns in the design industry.",
      },
      {
        subtitle: "Third-Party Rights",
        text: "Users are responsible for ensuring that any implementation of AI-generated designs does not infringe on third-party intellectual property rights, including copyrighted designs, trademarked products, or patented innovations.",
      },
      {
        subtitle: "Commercial Use",
        text: "If you plan to use AI-generated designs for commercial purposes, you should verify that all elements are appropriate for commercial use and do not infringe on any third-party rights.",
      },
    ],
  },
  {
    id: "training-data",
    title: "AI Training and Data Usage",
    icon: Lightbulb,
    content: [
      {
        subtitle: "Training Data Sources",
        text: "Our AI models are trained on diverse datasets including publicly available interior design images, licensed design databases, user-contributed content (with appropriate permissions), and professional design portfolios and publications.",
      },
      {
        subtitle: "Bias and Representation",
        text: "Despite our efforts to use diverse training data, AI models may reflect biases present in training datasets. This could result in overrepresentation of certain design styles or cultural aesthetics, underrepresentation of accessibility features or adaptive design elements, and bias toward certain price points or market segments.",
      },
      {
        subtitle: "Continuous Improvement",
        text: "We regularly update and retrain our AI models to improve accuracy, reduce bias, and incorporate new design trends and styles.",
      },
      {
        subtitle: "User Data",
        text: "With your consent, anonymized and aggregated user interactions may be used to improve our AI models. Personal information is handled according to our Privacy Policy.",
      },
    ],
  },
  {
    id: "technical-limitations",
    title: "Technical Limitations and Performance",
    icon: Zap,
    content: [
      {
        subtitle: "Processing Limitations",
        text: "AI generation requires significant computational resources. During high-demand periods, processing times may be longer, or service may be temporarily unavailable.",
      },
      {
        subtitle: "Image Quality",
        text: "The quality of AI-generated visualizations depends on various factors including quality and resolution of input images, lighting conditions in uploaded photos, complexity of the requested design changes, and current capabilities of our AI models.",
      },
      {
        subtitle: "Rendering Accuracy",
        text: "AI-generated visualizations are artistic interpretations and may not precisely represent how actual materials, lighting, or furniture would appear in your space.",
      },
      {
        subtitle: "Technology Evolution",
        text: "AI technology is rapidly evolving. Features, capabilities, and output quality may change as we update our models and systems.",
      },
    ],
  },
  {
    id: "ethical-considerations",
    title: "Ethical AI Use and Considerations",
    icon: Users,
    content: [
      {
        subtitle: "Responsible AI Development",
        text: "We are committed to developing and deploying AI technology responsibly, including regular auditing of AI outputs for bias and fairness, transparency about AI capabilities and limitations, protecting user privacy and data rights, and ensuring AI complements rather than replaces human creativity.",
      },
      {
        subtitle: "Environmental Impact",
        text: "We strive to minimize the environmental impact of our AI operations through efficient computing practices and sustainable infrastructure choices.",
      },
      {
        subtitle: "Human-AI Collaboration",
        text: "Our AI is designed to augment human creativity and decision-making, not replace professional expertise or personal judgment.",
      },
      {
        subtitle: "Accessibility",
        text: "We work to ensure our AI-generated designs consider accessibility needs, though users should always consult accessibility experts for specific requirements.",
      },
    ],
  },
  {
    id: "updates-changes",
    title: "AI Model Updates and Changes",
    icon: Eye,
    content: [
      {
        subtitle: "Model Evolution",
        text: "Our AI models are continuously updated and improved. This means design outputs may vary over time as models are enhanced, new features and capabilities may be added, and older design generations may not be reproducible with updated models.",
      },
      {
        subtitle: "Notification of Changes",
        text: "We will notify users of significant changes to AI capabilities through platform announcements, email notifications (for subscribed users), and updates to this disclaimer.",
      },
      {
        subtitle: "Backward Compatibility",
        text: "While we strive to maintain consistency, we cannot guarantee that future AI versions will produce identical results to previous versions.",
      },
      {
        subtitle: "Version Control",
        text: "We maintain version information for our AI models, but specific model versions used for individual designs may not be permanently preserved.",
      },
    ],
  },
]

export function AIDisclaimerPage() {
  const lastUpdated = "December 9, 2024"
  // Define header height constant for consistent offset
  const HEADER_HEIGHT = 80

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)

    // Add event listeners for smooth scrolling to anchor links
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === "A" && target.getAttribute("href")?.startsWith("#")) {
        e.preventDefault()
        const href = target.getAttribute("href")
        const targetId = href?.substring(1)
        const targetElement = document.getElementById(targetId || "")

        if (targetElement) {
          const yOffset = -HEADER_HEIGHT
          const y = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: "smooth" })
        }
      }
    }

    document.addEventListener("click", handleAnchorClick)

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener("click", handleAnchorClick)
    }
  }, [])

  return (
    <div className="container px-4 py-8 mx-auto md:px-6 md:py-12 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">AI Disclaimer</h1>
        <p className="text-lg text-gray-600 mb-2">
          Important information about our AI-powered design technology and its limitations
        </p>
        <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
      </div>

      {/* Quick Navigation */}
      <Card className="mb-8 bg-[#40A1AB]/5">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-[#40A1AB] hover:text-[#3A919A] transition-colors"
              >
                {section.title}
              </a>
            ))}
            <a href="#contact" className="text-[#40A1AB] hover:text-[#3A919A] transition-colors">
              Questions About Our AI Technology
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Introduction */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Roomvia utilizes advanced artificial intelligence (AI) and machine learning technologies to provide
              interior design recommendations, visualizations, and suggestions. This disclaimer explains the nature,
              capabilities, and limitations of our AI-powered services.
            </p>
            <p>
              By using Roomvia's AI-powered features, you acknowledge that you understand and accept the limitations and
              considerations outlined in this disclaimer. AI-generated content should be used as inspiration and
              guidance, not as definitive professional advice.
            </p>
            <p>
              We are committed to transparency about our AI technology and encourage users to make informed decisions
              about implementing AI-generated design recommendations.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Sections */}
      <div className="space-y-8">
        {sections.map((section) => {
          const Icon = section.icon
          return (
            <Card key={section.id} id={section.id} className="scroll-mt-[80px]">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-[#40A1AB]/10 p-2 rounded-lg">
                    <Icon className="h-6 w-6 text-[#40A1AB]" />
                  </div>
                  <h2 className="text-2xl font-semibold">{section.title}</h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, index) => (
                    <div key={index}>
                      <h3 className="text-lg font-medium mb-2 text-gray-900">{item.subtitle}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Contact Information */}
      <Card className="mt-8 scroll-mt-[80px]" id="contact">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Questions About Our AI Technology</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              If you have questions about our AI technology, its capabilities, or this disclaimer, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium">Roomvia AI Team</p>
              <p>Email: support@roomvia.ai</p>
            </div>
            <p>
              We welcome feedback about our AI technology and are committed to continuous improvement and transparency
              in our AI development practices.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Important Notice */}
      <Card className="mt-8 border-orange-200 bg-orange-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-orange-800 mb-2">Important Notice</h3>
              <p className="text-orange-700 leading-relaxed">
                AI-generated designs are for inspiration and guidance only. Always consult with qualified professionals
                before implementing any design changes, especially those involving structural modifications, electrical
                work, or significant renovations. Roomvia is not liable for any issues arising from the implementation
                of AI-generated designs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
